import { InertiaLinkProps, Link } from "@inertiajs/react";
import { Path } from "@/utils/definePathHelper";
import { Spinner } from "./Spinner";
import styles from "./Button.module.css";
import { clsx } from "clsx";

type ButtonVariant = "default" | "primary" | "destructive" | "ghost" | "transparent";
type CommonProps = {
  variant?: ButtonVariant;
  loading?: boolean;
  size?: "default" | "small";
};

export type ButtonProps =
  | (CommonProps & {
      as?: "button";
      href?: never;
      csfr?: boolean;
      ref?: React.Ref<HTMLButtonElement>;
    } & React.ComponentPropsWithoutRef<"button">)
  | (CommonProps & {
      as: "link";
      variant?: ButtonVariant;
      ref?: React.Ref<HTMLAnchorElement>;
    } & InertiaLinkProps &
      React.ComponentPropsWithoutRef<"a">)
  | (CommonProps & {
      as: "csfr";
      variant?: ButtonVariant;
      action: Path;
      ref?: React.Ref<HTMLButtonElement>;
    } & React.ComponentPropsWithoutRef<"button">);

export function Button({
  size = "default",
  variant = "default",
  className,
  children,
  ...props
}: ButtonProps) {
  if (props.as === "link") {
    const { ref, as: _, ...rest } = props;

    return (
      <Link
        ref={ref}
        data-loading={props.loading}
        data-variant={variant}
        data-size={size}
        className={clsx(styles.buttonRoot, className)}
        {...rest}
      >
        {children}
        {props.loading && <Spinner className={styles.spinner} />}
      </Link>
    );
  }

  if (props.as === "csfr") {
    var csrf_token = document.getElementsByName("csrf-token")[0].getAttribute("content") ?? "";

    return (
      <form method="post" className={styles.csrfButtonForm} action={props.action.path()}>
        <Button loading={props.loading} size={size} variant={variant}>
          {children}
          {props.loading && <Spinner className={styles.spinner} />}
        </Button>
        <input type="hidden" name="_method" value={props.action.httpMethod} />
        <input type="hidden" name="authenticity_token" value={csrf_token} />
      </form>
    );
  }

  const { ref } = props;

  return (
    <button
      ref={ref}
      data-loading={props.loading}
      data-variant={variant}
      data-size={size}
      className={clsx(styles.buttonRoot, className)}
      {...props}
    >
      {children}
      {props.loading && <Spinner className={styles.spinner} />}
    </button>
  );
}
